import React, { useRef, useEffect, useState } from 'react';
import note_blue from '../../images/note_blue.svg';
import tag_grey from '../../images/tag_grey.svg';
import editIcon from '../../images/editDisable.svg';
import editGreem from '../../images/edit-green.svg';
import deleteGreen from '../../images/delete-green.svg';

import styled from '@emotion/styled';
import { Button, Heading, Icon } from '../../common';
import { Checkbox } from '../../components/form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import EditingTag from '../Project/EditingTagAnalysis';

const Container = styled.div`
	position: relative;
	overflow: visible;
	background: var(--midWhite);
	margin-left: 0;
	margin-bottom: 1rem;
	label {
		margin-bottom: 0 !important;
	}
	.img {
		position: relative;
	}
	.video_play {
		position: absolute;
		left: 40%;
		top: 38%;
	}
	.iconContainer {
		display: flex;
		align-items: center;
		justify-content: center;
		.imgContainer {
			background: var(--darkWhite);
			margin-left: 5px;
			padding: 10px;
			width: 28px;
			height: 28px;
			border-radius: 14px;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			border: 1px solid transparent;

			&.active {
				background: var(--white);
				border-color: var(--greyBlue);
			}
		}
	}

	.accordionPanel {
		position: absolute;
		left: 0.5rem;
		right: 0.5rem;
		top: 100%;
		z-index: 30;
		box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.18);
		border-radius: 5px;
		background: var(--white);
		font-size: 10px;
		font-weight: 500;
		color: var(--deepBlue);
		padding: 0.5rem;
		margin: 4px 0 0;
	}
`;

const PrimaryButton = styled(Button)`
	padding: 2px 4px;
	&:hover {
		color: var(--darkGrey);
		span {
			color: var(--darkGrey);
		}
	}
	&:focus {
		box-shadow: none;
		background: transparent !important;
		color: var(--middleGreen);

		:hover {
			background: var(--middleGreen) !important;
			color: var(--darkGrey);
			span {
				color: var(--darkGrey);
			}
		}
	}

	&.time {
		color: var(--middleGrey);
		background: #101010;
		border: 0.5px solid #8a8a8a;
	}

	:disabled,
	[disabled] {
		color: var(--mediumGrey);
		border: 1px solid var(--mediumGrey);
		background: transparent;
		span {
			color: var(--mediumGrey);
		}
		:hover {
			box-shadow: none;
			background: transparent;
			color: var(--mediumGrey);
			cursor: not-allowed;
		}
	}
`;

const CustomButton = styled(Button)`
	color: ${props => (props.disabled ? 'var(--lightGrey)' : props.buttonColor)};
	border-radius: ${props => (props.borderRadius ? props.borderRadius : '2px')};

	&:focus {
		background: transparent !important;
		&:hover {
			color: var(--white);
		}
	}
	&:hover {
		color: var(--quadColor);
		background: transparent !important;
		box-shadow: none;
	}
	&.time {
		background: #101010;
		border: 0.5px solid #8a8a8a;
	}

	:disabled,
	[disabled] {
		color: var(--mediumGrey);
		border: 1px solid var(--mediumGrey);
		background: transparent;
		:hover {
			box-shadow: none;
			background: transparent;
			color: var(--mediumGrey);
			cursor: not-allowed;
		}
	}
`;

const TextArea = styled.textarea`
	border: 0;
	background: transparent;
	resize: none;
	font-size: 11px;
	font-weight: 500;
	width: 90%;
	max-width: 90%;
	min-width: 150px;
	margin: 0;
	padding: 0;
	overflow-wrap: break-word;
	white-space: pre-wrap;
	overflow-y: scroll;
	&::-webkit-scrollbar {
		display: none;
	}

	&:focus-visible {
		border: 0;
		outline: none;
	}
`;

const FormContainer = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 0;
	margin: 0;
	width: 100%;
	overflow: hidden;
	border-bottom: ${props => (props.disabled ? '1px solid transparent' : '1px solid var(--middleGreen)')};
	textarea {
		min-width: 150px;
		flex: 1;
	}
	span {
		align-self: flex-end;
		margin-bottom: 0.5rem;
	}
	.icon {
		cursor: pointer;
		flex-shrink: 0;
	}
`;

const IconCustom = styled(Icon)`
	font-size: 0.75rem;
	margin-top: 5px;
`;

const TagButton = styled(CustomButton)`
	border: none;
	display: flex;
	align-items: center;
	color: #8a8a8a;
	padding: 4px 8px;

	span {
		font-size: 17px;
		color: #8a8a8a;
		padding-right: 3px;
	}
`;

const EditInputForm = ({
	className,
	disabled,
	value,
	filters,
	projectId,
	enableDisableNoteEdit,
	noteUpdated,
	clipData,
	questionType_ID
}) => {
	const [text, setText] = useState(value);
	const noteForm = useRef();
	const dispatch = useDispatch();
	const loggedUser = useSelector(state => state.LoginReducer);

	useEffect(() => {
		noteForm.current.value = clipData.VideoNote ? clipData.VideoNote : '';
	}, [clipData]);

	const handleSubmit = e => {
		e.preventDefault();
		const data1 = {
			user_ID: loggedUser.userId,
			videoNote: noteForm.current.value,
			clip_ID: clipData?.projectRepondentVideo_ID || clipData?.questionAnswerImage_ID,
			questionType_ID
		};

		dispatch({ type: 'REQUEST_ADDVIDEONOTE', payload: data1 });
		setTimeout(() => {
			dispatch({
				type: 'REQUEST_ANALYSIS_CLIPS_WITHOUT_URL',
				payload: {
					project_ID: projectId,
					AgeGroup: filters['AgeGroup'],
					MaritalStatus: filters['MaritalStatus'],
					Gender: filters['Gender'],
					classificationData: filters['classificationData'],
					UserType: filters['UserType'],
					Geography: filters['Geography'],
					Ownership: filters['Ownership'],
					CustomTags: filters['CustomTags']
				}
			});
		}, 500);
		noteUpdated(clipData, noteForm.current.value);
		enableDisableNoteEdit();
	};

	return (
		<div>
			<FormContainer className={`${className} ${!disabled && 'mb-2'}`} disabled={disabled}>
				<form onSubmit={handleSubmit} style={{ width: '90%' }}>
					<TextArea
						disabled={disabled}
						defaultValue={value}
						rows={!disabled && 1}
						placeholder="Add a new note"
						onChange={e => setText(e.target.value)}
						ref={noteForm}
					/>
				</form>
				{!disabled && (
					<div onClick={handleSubmit} className="icon">
						<IconCustom color="var(--sapGreen)" style={{ height: '8px' }}>
							subdirectory_arrow_left
						</IconCustom>
					</div>
				)}
			</FormContainer>
		</div>
	);
};

function AnalysisAudio({
	checkBoxValue,
	imgLink,
	onPlay,
	selectCheckBox,
	name,
	imgId,
	qid,
	selection,
	Skeleton,
	questionType_ID,
	filters,
	tagsFilterdata,
	clipWithoutUrl,
	addtag,
	openPanel,
	onToggleAccordion,
	onCloseAccordion
}) {
	const [ref, inView] = useInView({
		triggerOnce: true,
		threshold: 0.5
	});
	const openAccordion = openPanel ?? null;
	const containerRef = useRef(null);
	const [clipData, setClipData] = useState({ tags: [], VideoNote: null });
	const [enableNoteEdit, setEnableNoteEdit] = useState(true);
	const [addNewNote, setAddNewNote] = useState(false);
	const loggedUser = useSelector(state => state.LoginReducer);
	const { id: routeProjectId } = useParams();
	const storeProjectId = useSelector(state => state.ProjectReducer.project_ID);
	const projectId = routeProjectId || storeProjectId;
	const dispatch = useDispatch();

	useEffect(() => {
		const normalizedClipTags = clipWithoutUrl?.tags
			? clipWithoutUrl.tags.map(tag => ({
					tag_ID: tag.tag_ID,
					tagName: tag.tag || tag.tagName,
					table_ID: tag.table_ID
			  }))
			: [];

		const normalizedAddTags = addtag?.tags?.map(tag => ({
			...tag,
			tagName: tag.tag || tag.tagName,
			table_ID: tag.table_ID || null
		})) || [];

		const mergedTags = [...normalizedClipTags, ...normalizedAddTags];
		const dedupedTags = mergedTags.filter((tag, index, arr) => {
			const key = `${tag.table_ID || ''}-${tag.tag_ID || ''}`;
			return index === arr.findIndex(t => `${t.table_ID || ''}-${t.tag_ID || ''}` === key);
		});

		setClipData({
			...(clipWithoutUrl || {}),
			...(addtag || {}),
			tags: dedupedTags
		});
	}, [clipWithoutUrl, addtag]);

	const handleAddNote = () => {
		setAddNewNote(true);
		enableDisableNoteEdit();
	};

	const toggleAccordion = panel => {
		onToggleAccordion?.(panel);
	};

	useEffect(() => {
		if (!openAccordion) return;
		const handleOutsideClick = e => {
			if (containerRef.current && !containerRef.current.contains(e.target)) {
				onCloseAccordion?.();
			}
		};
		document.addEventListener('mousedown', handleOutsideClick);
		return () => document.removeEventListener('mousedown', handleOutsideClick);
	}, [openAccordion, onCloseAccordion]);

	const enableDisableNoteEdit = () => {
		setEnableNoteEdit(!enableNoteEdit);
	};

	const noteUpdated = (currentClipData, note, type) => {
		if (type && type === 'delete') {
			setClipData({ ...currentClipData, VideoNote: null });
		} else {
			setClipData({ ...currentClipData, VideoNote: note });
		}
	};

	const updateTagList = updatedTagList => {
		const updatedClipData = { ...clipData, tags: updatedTagList };
		window.isTagAdded = true;
		window.dispatchEvent(new Event('tagAdded'));
		setClipData(updatedClipData);
	};

	return (
		<div ref={ref} style={openAccordion ? { position: 'relative', zIndex: 30 } : undefined}>
			{inView ? (
				<Container ref={containerRef}>
					<div className="audio">
						<audio controls style={{ width: '100%', maxHeight: '250px' }} onPlay={onPlay}>
							<source src={imgLink} type="audio/mpeg" />
						</audio>
					</div>

					<div className="d-flex p-2 justify-content-between">
						<div className="d-flex align-items-center">
							<Checkbox
								color="var(--deepBlue)"
								fontSize="10px"
								iconSize="10px"
								inactiveColor="var(--white)"
								value={imgId}
								checked={Object.keys(selection).indexOf(imgId.toString()) != -1}
								onChange={() => {
									selectCheckBox({
										path: imgLink,
										name: name,
										imgId: imgId
									});
								}}
							/>
							<div className="pl-1 d-flex">
								<Heading
									fontSize="10px"
									fontWeight="700"
									color="var(--deepBlue)"
									className="mb-0 pb-0 pl-1"
									breakHeading={false}
								>
									{name}
								</Heading>
							</div>
						</div>

						<div className="iconContainer">
							<div
								className={`imgContainer ${openAccordion === 'tag' ? 'active' : ''}`}
								role="button"
								aria-expanded={openAccordion === 'tag'}
								onClick={() => toggleAccordion('tag')}
							>
								<img src={tag_grey} alt="Tags" />
							</div>
							<div
								className={`imgContainer ${openAccordion === 'note' ? 'active' : ''}`}
								role="button"
								aria-expanded={openAccordion === 'note'}
								onClick={() => toggleAccordion('note')}
							>
								<img src={note_blue} alt="Notes" />
							</div>
						</div>
					</div>
					{openAccordion === 'tag' && (
						<div className="accordionPanel">
							<EditingTag
								singleValueStyle={{
									fontSize: '10px',
									fontWeight: '700',
									color: 'var(--greyBlue)'
								}}
								tagColor="var(--greyBlue)"
								addTagButtonColor="#8A8A8A"
								tagList={clipData?.tags}
								onUpdateTagList={updateTagList}
								filters={filters}
								clipData={clipData}
								Options={tagsFilterdata?.CustomTags}
								questionType_ID={questionType_ID}
								qid={qid}
								AddTagButton={() => (
									<TagButton
										fontSize="12px"
										fontWeight="300"
										variant="outlined"
										buttonColor="#8A8A8A"
										color="#8A8A8A"
									>
										<Icon>add</Icon>
									</TagButton>
								)}
							/>
						</div>
					)}
					{openAccordion === 'note' && (
						<div className="accordionPanel">
							{clipData && !clipData.VideoNote && (
								<PrimaryButton
									fontWeight="700"
									fontSize="10px"
									size="small"
									variant="outlined"
									className="px-0 py-0 pr-1 d-inline-flex align-items-center"
									borderRadius="3px"
									buttonColor="var(--middleGreen)"
									onClick={handleAddNote}
								>
									<Icon style={{ fontSize: '16px', marginRight: '3px' }} color="var(--middleGreen)">
										add
									</Icon>
									Add Note
								</PrimaryButton>
							)}
							{clipData && clipData.VideoNote && (
								<div>
									<div className="d-flex align-items-center justify-content-between">
										<Heading
											type={3}
											fontSize="9px"
											fontWeight="500"
											color="var(--middleGrey)"
											className="mb-0 pb-0"
										>
											Note
										</Heading>
										<div className="d-flex align-items-center">
											<img
												src={enableNoteEdit ? editIcon : editGreem}
												style={{ cursor: 'pointer', marginRight: '5px' }}
												onClick={() => {
													enableDisableNoteEdit();
												}}
											/>
											<img
												style={{ cursor: 'pointer' }}
												src={deleteGreen}
												onClick={() => {
													dispatch({
														type: 'REQUEST_DELETEVIDEONOTE',
														payload: {
															user_ID: loggedUser.userId,
															videoNotes_ID: clipData?.projectRepondentVideo_ID || clipData?.questionAnswerImage_ID,
															questionType_ID
														}
													});

													setTimeout(() => {
														dispatch({
															type: 'REQUEST_ANALYSIS_CLIPS_WITHOUT_URL',
															payload: {
																project_ID: projectId,
																AgeGroup: filters['AgeGroup'],
																MaritalStatus: filters['MaritalStatus'],
																Gender: filters['Gender'],
																classificationData: filters['classificationData'],
																UserType: filters['UserType'],
																Geography: filters['Geography'],
																Ownership: filters['Ownership'],
																CustomTags: filters['CustomTags']
															}
														});
													}, 500);

													noteUpdated(clipData, null, 'delete');
												}}
											/>
										</div>
									</div>
									<EditInputForm
										className="mb-0 pb-0 mt-2"
										disabled={enableNoteEdit}
										value={clipData.VideoNote}
										clipData={clipData}
										filters={filters}
										projectId={projectId}
										enableDisableNoteEdit={enableDisableNoteEdit}
										noteUpdated={noteUpdated}
										questionType_ID={questionType_ID}
									/>
								</div>
							)}
							{addNewNote && !clipData?.VideoNote && (
								<div>
									<EditInputForm
										className="mb-0 pb-0 mt-2"
										disabled={enableNoteEdit}
										value={clipData.VideoNote}
										filters={filters}
										projectId={projectId}
										enableDisableNoteEdit={enableDisableNoteEdit}
										noteUpdated={noteUpdated}
										clipData={clipData}
										questionType_ID={questionType_ID}
									/>
								</div>
							)}
						</div>
					)}
				</Container>
			) : (
				Skeleton
			)}
		</div>
	);
}

export default AnalysisAudio;